import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Snackbar, Alert, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import OfflineStorage from '../services/OfflineStorage';
import api from '../services/api';

const SyncManager = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [syncing, setSyncing] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [notificationSeverity, setNotificationSeverity] = useState('info');
    const syncingRef = useRef(false); // guardia anti re-entrancy del sync
    const token = useSelector((state) => state.auth.token);
    const tokenPrecedente = useRef(token);

    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            attemptSync();
        };

        const handleOffline = () => {
            setIsOnline(false);
            setNotificationMessage('Sei offline. Le modifiche verranno salvate nel dispositivo.');
            setNotificationSeverity('warning');
            setShowNotification(true);
        };

        const handleQueued = () => {
            setNotificationMessage('Salvato sul dispositivo. Verrà inviato automaticamente appena torni online.');
            setNotificationSeverity('info');
            setShowNotification(true);
        };
        const handleQueueFailed = () => {
            setNotificationMessage('Memoria del dispositivo piena: impossibile salvare offline. Riprova quando torni online.');
            setNotificationSeverity('error');
            setShowNotification(true);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        window.addEventListener('offline-queued', handleQueued);
        window.addEventListener('offline-queue-failed', handleQueueFailed);

        // Check if we have pending requests on mount/startup
        if (navigator.onLine) {
            attemptSync();
        }

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            window.removeEventListener('offline-queued', handleQueued);
            window.removeEventListener('offline-queue-failed', handleQueueFailed);
        };
    }, []);

    // Dopo l'accesso il componente NON viene rimontato (il login naviga senza
    // ricaricare la pagina): senza questo, la coda resterebbe ferma fino al
    // prossimo riavvio dell'app.
    useEffect(() => {
        const primaNonCera = !tokenPrecedente.current;
        tokenPrecedente.current = token;
        if (token && primaNonCera && navigator.onLine) {
            attemptSync();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const attemptSync = async () => {
        if (syncingRef.current) return; // evita sync concorrenti (mount + evento online)

        // Senza sessione non ha senso rimandare richieste autenticate: il server
        // risponderebbe 401 per ognuna. È quello che bloccava i tablet con la coda
        // piena e il token scaduto: si apriva il login, partiva il sync, arrivava
        // il 401, la pagina si ricaricava e si ricominciava, all'infinito.
        if (!localStorage.getItem('token')) return;

        const queue = OfflineStorage.getQueue();
        if (queue.length === 0) return;

        syncingRef.current = true;
        setSyncing(true);
        setNotificationMessage(`Tornato online. Sincronizzazione di ${queue.length} elementi in corso...`);
        setNotificationSeverity('info');
        setShowNotification(true);

        let successCount = 0;
        const failed = [];

        // In sequenza, per mantenere l'ordine
        for (const req of queue) {
            try {
                // L'interceptor di richiesta ri-attacca un token fresco; serve solo method/url/data
                await api({
                    method: req.method,
                    url: req.url,
                    data: req.data,
                    params: req.params,
                    // marcatore letto dall'interceptor: un 401 qui non deve
                    // reindirizzare, ci pensa il `break` qui sotto
                    replayCodaOffline: true,
                });
                OfflineStorage.removeFromQueue(req.id);
                successCount++;
            } catch (error) {
                const status = error.response?.status;
                if (status === 401) {
                    // Sessione non valida: mi fermo e lascio TUTTO in coda, riparte dopo
                    // il nuovo accesso. Nessuna perdita di dati. L'interceptor NON
                    // reindirizza per le richieste rimandate (replayCodaOffline),
                    // altrimenti si innescherebbe il ciclo di ricariche sul login.
                    break;
                }
                if (status >= 400 && status < 500) {
                    // Rifiuto definitivo (validazione/duplicato/permesso): togliamo dalla coda per non
                    // bloccarla, ma teniamo il motivo da mostrare all'utente.
                    OfflineStorage.removeFromQueue(req.id);
                    failed.push(error.response?.data?.message || `${(req.method || '').toUpperCase()} ${req.url} (${status})`);
                }
                // 5xx o rete ancora assente: resta in coda, si riprova al prossimo sync
            }
        }

        syncingRef.current = false;
        setSyncing(false);
        if (successCount > 0 || failed.length > 0) {
            if (failed.length === 0) {
                setNotificationMessage(`Sincronizzazione completata: ${successCount} inviati.`);
                setNotificationSeverity('success');
            } else {
                setNotificationMessage(`Sincronizzati ${successCount}. ${failed.length} non riusciti: ${failed.join('; ')}`);
                setNotificationSeverity('warning');
            }
            setShowNotification(true);
        }
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setShowNotification(false);
    };

    return (
        <Snackbar
            open={showNotification}
            autoHideDuration={6000}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
            <Alert
                onClose={handleClose}
                severity={notificationSeverity}
                variant="filled"
                sx={{ width: '100%' }}
                action={
                    syncing ? <CircularProgress size={20} color="inherit" /> :
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                }
            >
                {notificationMessage}
            </Alert>
        </Snackbar>
    );
};

export default SyncManager;
